/**
 * EO «Финансы» — полноэкранные модалки списков (паттерн ReceiptsOverlay C13):
 *  • GamesListModal — «Отведённые игры» (games-list): новые сверху, дата-
 *    сепараторы с днём недели, бейдж основного способа оплаты (max из cash/
 *    terminal/transfer/prepaid_used/invoice/other; other игры 1С — «Проч. 1С», payShare.ts), фон строки по расхождению
 *    |total − expected_total| > 0.5 (V1-логика), футер: расхождения + итог.
 *  • FutureBookingsModal — «Предстоящие игры» (future-bookings): группировка по
 *    датам, выходные подсвечены, предоплата/нет, дневные итоги + общий итог.
 * Обе read-only; данные приходят из useFinanceGames (кэш на период).
 */
import { Fragment, type ReactNode } from "react";
import type { FinFutureRow, FinGameRow, FinListState } from "../../hooks/useFinanceGames";
import { PlayersBadge } from "../../components/shared";
import css from "./finance.module.css";
import { BADGE_LABEL, payBadge, type PayKey } from "./payShare";

const rub = (n: number) => `₽${Math.round(n || 0).toLocaleString("ru-RU")}`;
const DOW = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const dow = (iso: string): string => DOW[new Date(`${iso}T12:00:00Z`).getUTCDay()] ?? "";
const dm = (iso: string): string => { const [, m, d] = iso.split("-"); return `${d}.${m}`; };

function OverlayShell({ title, meta, accent, onClose, footer, children }: {
  title: string; meta: string; accent: string | undefined;
  onClose: () => void; footer?: ReactNode; children: ReactNode;
}) {
  return (
    <div className={css.ovBack}>
      <div className={css.ovHead}>
        <div>
          <div className={`${css.ovTitle} ${accent ?? ""}`}>{title}</div>
          <div className={css.ovMeta}>{meta}</div>
        </div>
        <button type="button" className={css.ovClose} aria-label="Закрыть" onClick={onClose}>✕</button>
      </div>
      <div className={css.ovBody}>{children}</div>
      {footer ? <div className={css.ovFooter}>{footer}</div> : null}
    </div>
  );
}

function OvStates<T>({ state, empty, onRetry }: { state: FinListState<T>; empty: string; onRetry: () => void }) {
  if (state.loading) return <div className={css.ovState}>Загрузка…</div>;
  if (state.error) {
    return (
      <div className={css.ovState}>
        Ошибка: {state.error}<br />
        <button type="button" className={css.retry} onClick={onRetry}>Повторить</button>
      </div>
    );
  }
  if (state.rows && state.rows.length === 0) return <div className={css.ovState}>{empty}</div>;
  return null;
}

/* Основной способ оплаты игры — метод с максимальной суммой (V1:7697-7712); «Проч. 1С» — цвет бейджа по умолчанию (приглушённый). */
const BADGE_CLS: Record<PayKey, string | undefined> = {
  cash: css.payGold, terminal: css.payBlue, transfer: css.payPurple, prepaid: css.payOrange, invoice: css.payGreen, other: css.payCyan, other1c: undefined,
};
function payMethod(g: FinGameRow): { label: string; cls: string | undefined } {
  const k = payBadge(g);
  return k ? { label: BADGE_LABEL[k], cls: BADGE_CLS[k] } : { label: "—", cls: undefined };
}

const isDiff = (g: FinGameRow) => Math.abs((Number(g.total) || 0) - (Number(g.expected_total) || 0)) > 0.5;

export function GamesListModal({ from, to, state, onClose, onRetry }: {
  from: string; to: string; state: FinListState<FinGameRow>; onClose: () => void; onRetry: () => void;
}) {
  const sorted = [...(state.rows ?? [])].sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));
  const diffs = sorted.filter(isDiff).length;
  const sum = sorted.reduce((s, g) => s + (Number(g.total) || 0), 0);
  return (
    <OverlayShell
      title="ОТВЕДЁННЫЕ ИГРЫ" accent={css.green} onClose={onClose}
      meta={`${from} — ${to}${state.rows ? ` · ${sorted.length} игр` : ""}`}
      footer={sorted.length > 0 ? (
        <>
          <span className={css.ovFootNote}>{diffs > 0 ? `Расхождений: ${diffs}` : "Без расхождений"}</span>
          <span className={`${css.ovFootSum} ${css.green}`}>{rub(sum)}</span>
        </>
      ) : null}
    >
      <OvStates state={state} empty="Нет игр за период" onRetry={onRetry} />
      {sorted.map((g, i) => {
        const sep = i === 0 || sorted[i - 1]!.date !== g.date;
        const pay = payMethod(g);
        return (
          <Fragment key={`${g.report_id}-${i}`}>
            {sep && (
              <div className={css.dateSep}>
                <span className={css.dateSepD}>{dm(g.date)}</span>
                <span className={css.dateSepW}>{dow(g.date)}</span>
              </div>
            )}
            <div className={`${css.gRow} ${isDiff(g) ? css.gDiff : css.gOk}`}>
              <span className={css.gTime}>{g.time}</span>
              <span className={css.gQuest}>{g.quest}</span>
              <span className={`${css.payBadge} ${pay.cls ?? ""}`}>{pay.label}</span>
              <span className={css.gSum}>{rub(Number(g.total) || 0)}</span>
            </div>
          </Fragment>
        );
      })}
    </OverlayShell>
  );
}

export function FutureBookingsModal({ from, to, state, onClose, onRetry }: {
  from: string; to: string; state: FinListState<FinFutureRow>; onClose: () => void; onRetry: () => void;
}) {
  const rows = state.rows ?? [];
  const byDate = new Map<string, FinFutureRow[]>();
  for (const g of rows) {
    const arr = byDate.get(g.date);
    if (arr) arr.push(g); else byDate.set(g.date, [g]);
  }
  const dates = [...byDate.keys()].sort();
  const grand = rows.reduce((s, g) => s + (Number(g.est_revenue) || 0), 0);
  return (
    <OverlayShell
      title="ПРЕДСТОЯЩИЕ ИГРЫ" accent={css.blue} onClose={onClose}
      meta={`${from} — ${to}${state.rows ? ` · ${rows.length} игр` : ""}`}
      footer={rows.length > 0 ? (
        <>
          <span className={css.ovFootNote}>{rows.length} игр за период</span>
          <span className={`${css.ovFootSum} ${css.blue}`}>{rub(grand)}</span>
        </>
      ) : null}
    >
      <OvStates state={state} empty="Нет будущих броней за период" onRetry={onRetry} />
      {dates.map((date) => {
        const day = byDate.get(date)!;
        const we = dow(date) === "сб" || dow(date) === "вс";
        const dayTotal = day.reduce((s, g) => s + (Number(g.est_revenue) || 0), 0);
        return (
          <div key={date}>
            <div className={`${css.daySep} ${we ? css.daySepWe : ""}`}>
              <span className={css.daySepD}>{dm(date)} {dow(date)}</span>
              <span className={css.daySepSum}>{rub(dayTotal)} · {day.length} игр</span>
            </div>
            {day.map((g) => (
              <div key={String(g.id)} className={css.fRow}>
                <span className={css.fTime}>{g.time}</span>
                <span className={css.fQuest}>{g.quest || g.quest_name}</span>
                {/* 05.09 (владелец): стандартный бейдж игроков; g.id = bookings.id (/api/finance/future-bookings), у ЕО есть доступ — клик открывает карточку клиента; .fPl держит ширину колонки */}
                <PlayersBadge count={g.players} bookingId={Number(g.id)} fallbackName={g.client_name} size="sm" className={css.fPl} />
                {g.prepaid
                  ? <span className={css.fPre}>{rub(Number(g.prepaid_amount) || 0)}</span>
                  : <span className={css.fNoPre}>нет</span>}
                <span className={css.fEst}>{rub(Number(g.est_revenue) || 0)}</span>
              </div>
            ))}
          </div>
        );
      })}
    </OverlayShell>
  );
}
